import { useTonConnectUI } from "@tonconnect/ui-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { userApi } from "@/api/user";
import { USER_QUERY_KEY, useUser } from "@/modules/shared/hooks/use-user";
import { logMsg, logEx, crumb } from "@/config/sentry-logger";

type DeleteUserResponse = Awaited<ReturnType<typeof userApi.deleteUser>>;

const useDisconnectWallet = () => {
  const [tonConnectUI] = useTonConnectUI();
  const navigate = useNavigate();
  const { user } = useUser();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteUser } = useMutation<
    DeleteUserResponse,
    unknown,
    void
  >({
    mutationKey: ["delete-user", user?.id],
    mutationFn: async () => {
      if (user?.id !== undefined && user?.id !== null) {
        crumb("deleteUser mutation start", { hasUserId: true });

        return userApi.deleteUser(user.id);
      }
      logMsg("deleteUser rejected: no-user-id", "warning", {
        hasUserId: false,
      });
      throw new Error("no-user-id");
    },
    onSuccess: () => {
      logMsg("deleteUser success", "info", { userId: user?.id ?? null });
      crumb("deleteUser success");
    },
    onError: (error) => {
      logMsg("deleteUser error", "warning", { userId: user?.id ?? null });
      logEx(error, "deleteUser mutation error", { hasUserId: !!user?.id });
    },
  });

  const [isDisconnectingWallet, setIsDisconnectingWallet] = useState(false);

  const disconnectWallet = async () => {
    setIsDisconnectingWallet(true);
    crumb("disconnectWallet start", {
      connected: tonConnectUI.connected,
      hasUserId: !!user?.id,
    });

    try {
      if (tonConnectUI.connected) {
        try {
          await tonConnectUI.disconnect();
          logMsg("tonConnectUI.disconnect success", "info");
          crumb("TON wallet disconnected");
        } catch (e) {
          logMsg("tonConnectUI.disconnect failed", "warning");
          logEx(e, "tonConnectUI.disconnect");
        }
      }

      try {
        await deleteUser();
      } catch (error) {
        toast.error("Could not delete the user");
        logMsg("deleteUser mutateAsync failed (caught outside)", "warning", {
          hasUserId: !!user?.id,
        });
        logEx(error, "deleteUser mutateAsync outer catch");
      }

      queryClient.removeQueries({ queryKey: [USER_QUERY_KEY] });
      crumb("navigate to / after disconnect");
      navigate("/");
    } catch (error) {
      toast.error("Could not disconnect from the wallet");
      logMsg("disconnectWallet outer try failed", "error");
      logEx(error, "disconnectWallet outer error");
    } finally {
      setIsDisconnectingWallet(false);
    }
  };

  return {
    disconnectWallet,
    isDisconnectingWallet,
  };
};

export { useDisconnectWallet };
